import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { formatISO, isValid, parseISO } from "date-fns";
import { EpisodeDownloads } from "~/components/charts/episode-downloads";
import { ChartFilters } from "~/components/charts/ui/chart-filters";
import {
	buildQuickFiltersData,
	QUICK_FILTERS,
	type QuickFilter,
	useChartFilters,
} from "~/hooks/user-chart-filters";
import { useTRPC } from "~/trpc/client";

type StatisticsSearch = {
	q?: QuickFilter;
	from?: string;
	to?: string;
};

export const Route = createFileRoute("/_app/podcasts/$channelUid/episodes/$episodeUid/statistics")({
	component: Page,
	validateSearch: (search: Record<string, unknown>): StatisticsSearch => {
		const q = typeof search.q === "string" && QUICK_FILTERS.includes(search.q) ? search.q : "7d";
		const quickFilters = buildQuickFiltersData();
		const quickFilter = !["all", "custom"].includes(q)
			? quickFilters.find((f) => f.key === q)
			: undefined;

		return {
			q,
			from:
				typeof search.from === "string" && isValid(parseISO(search.from))
					? search.from
					: quickFilter?.value?.from
						? formatISO(quickFilter.value.from, { representation: "date" })
						: undefined,
			to:
				typeof search.to === "string" && isValid(parseISO(search.to))
					? search.to
					: quickFilter?.value?.to
						? formatISO(quickFilter.value.to, { representation: "date" })
						: undefined,
		};
	},
});

function Page() {
	const search = Route.useSearch();
	const { episodeUid } = Route.useParams();
	const trpc = useTRPC();

	const { data: episode } = useQuery(trpc.episode.find.queryOptions({ episodeUid }));

	const chartFilters = useChartFilters({
		allStartDate: episode?.publishDate ? new Date(episode.publishDate) : new Date(),
		initialFilter: search.q,
		initialDate:
			search.from && search.to
				? { from: new Date(search.from), to: new Date(search.to) }
				: undefined,
	});

	const filtersWithEpisodeUid = {
		...chartFilters.filters,
		episodeUid: episodeUid,
	};

	return (
		<div className="grid w-full gap-6 px-4">
			<ChartFilters hook={chartFilters} />
			<div className="grid grid-cols-12 gap-6">
				<EpisodeDownloads className="col-span-full" filters={filtersWithEpisodeUid} />
			</div>
		</div>
	);
}
